import { META_DATA } from "src/metadata/constants"

const dataPrefix = "data:"
const placeholderOrigin = "https://placehold.co"
const sanityOrigin = "https://cdn.sanity.io"
const imagekitBaseUrl = "https://ik.imagekit.io/mkgs1djce"
const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_ORIGIN

export type ImageFit = "cover" | "contain"
export type ImageCdnOptions = ImageKitTransformationOptions

/**
 * @see
 * https://docs.imagekit.io/features/image-transformations/resize-crop-and-other-transformations
 * @see https://docs.imagekit.io/features/image-transformations/image-enhancement-and-color-manipulation
 */
type ImageKitTransformationOptions = {
  w?: number
  h?: number
  fit?: ImageFit
}

/**
 * @see
 * https://docs.imagekit.io/features/image-transformations/resize-crop-and-other-transformations
 * @see https://docs.imagekit.io/features/image-transformations/image-enhancement-and-color-manipulation
 */
type ImageKitTransformations = {
  w?: number
  h?: number
  cm?: "pad_resize"
  fo?: "auto"
}

export function getPlaceholderUrl(options: ImageKitTransformationOptions) {
  if (options.w || options.h) {
    return `https://placehold.co/${options.w || options.h}x${options.h || options.w}`
  }
  return "https://placehold.co/not-found"
}

export function imageRequiresCdn(url: string | null | undefined) {
  return (
    !!url &&
    !url.startsWith(dataPrefix) &&
    !url.startsWith(placeholderOrigin) &&
    !url.startsWith(sanityOrigin) &&
    !url.startsWith(imagekitBaseUrl) &&
    !url.includes("_next") &&
    !url.includes("localhost") &&
    !url.includes("fraudblocker") &&
    true
  )
}

function proxyImageThroughSanity(url: string, options?: ImageCdnOptions) {
  // https://www.sanity.io/docs/image-urls
  const transformations = new URLSearchParams()

  transformations.set("auto", "format")

  if (options?.w) {
    transformations.set("w", options.w.toString())
  }
  if (options?.h) {
    transformations.set("h", options.h.toString())
  }
  if (options?.w || options?.h) {
    if (options.fit === "cover") {
      transformations.set("fit", "crop")
    } else {
      transformations.set("fit", "max")
    }
  }

  return url.split("?")[0] + "?" + transformations.toString()
}

function proxyImageThroughImagekit(url: string, options: ImageKitTransformationOptions = {}) {
  // https://docs.imagekit.io/features/image-transformations/resize-crop-and-other-transformations
  // https://docs.imagekit.io/features/image-transformations/image-enhancement-and-color-manipulation
  const transformations: ImageKitTransformations = options

  if (transformations.w || transformations.h) {
    transformations.fo = "auto"

    if (options.fit === "contain") {
      transformations.cm = "pad_resize"
    }
  }

  const transformationsString = Object.entries(transformations)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}-${value}`)
    .join(",")

  const params = transformationsString ? `?tr=${transformationsString}` : ""

  if (url.startsWith(imagekitBaseUrl)) {
    return url.split("?")[0] + params
  }

  const path = new URL(url, imagekitBaseUrl).pathname

  return imagekitBaseUrl + path + params
}

export function toImageCdnUrl(url: string | null | undefined, options: ImageKitTransformationOptions = {}) {
  if (!url) {
    return getPlaceholderUrl(options)
  }
  if (url.startsWith(dataPrefix)) {
    return url
  }
  if (url.startsWith(placeholderOrigin)) {
    return url
  }
  if (url.startsWith("/") && frontendUrl !== META_DATA.origin) {
    return url
  }
  if (url.startsWith(sanityOrigin)) {
    return proxyImageThroughSanity(url, options)
  }
  return proxyImageThroughImagekit(url, options)
}
